import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from 'react-query';
import { mutateFunction } from '../../libs/apis';
import { CustomToast } from '../../components/toast/CustomToast';

const schema = yup.object({
  name: yup.string().required('Event name is required'),
  start_date: yup
    .date()
    .required('Start date is required')
    .min(new Date(), 'Start date cannot be in the past')
    .typeError('Please enter a valid start date'),
  end_date: yup
    .date()
    .required('End date is required')
    .min(yup.ref('start_date'), 'End date must be after start date')
    .typeError('Please enter a valid end date'),
  location: yup.string().required('Location is required'),
  description: yup.string().optional(),
  cover_image: yup.mixed().optional(),
  event_link: yup.string().url('Please enter a valid URL').optional(),
});

const CreateEventModal = ({
  isOpen,
  onClose,
  tabIndex,
  refetchEvents,
  refetchMyEvents,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      start_date: '',
      end_date: '',
      location: '',
      description: '',
      cover_image: null,
      event_link: '',
    },
  });

  const { mutate, isLoading: isLoadingMutation } = useMutation(mutateFunction);
  const { addToast } = CustomToast();

  const isLoading = isLoadingMutation || isSubmitting;

  // Get current date and time in the format required for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  };

  const onSubmit = async (data) => {
    console.log('eventdata', data);
    let formData = new FormData();

    // Add all form fields to FormData
    formData.append('name', data.name);
    formData.append('start_date', data.start_date);
    formData.append('end_date', data.end_date);
    formData.append('location', data.location);
    formData.append('description', data.description || '');
    formData.append('event_link', data.event_link || '');

    // Handle cover image if it exists
    if (data.cover_image?.[0]) {
      formData.append('cover_image', data.cover_image[0]);
    }

    mutate(
      {
        key: 'events/create',
        method: 'post',
        data: formData,
      },
      {
        onSuccess(res) {
          addToast(res);
          if (tabIndex === 0) {
            refetchEvents();
          } else {
            refetchMyEvents();
          }

          reset();
          onClose();
        },
        onError(e) {
          addToast(
            e?.response?.data?.message || 'Error creating event',
            'error',
          );
        },
      },
    );
  };

  const handleFormSubmit = async (data) => {
    await onSubmit(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        w={{ base: '90%', md: '500px' }}
        maxW={{ base: '90%', md: '500px' }}
        maxH="90vh"
        overflow="hidden"
      >
        <ModalHeader>Create New Event</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={errors.name}>
                <FormLabel>Event Name</FormLabel>
                <Input {...register('name')} placeholder="Enter event name" />
                <FormErrorMessage>
                  {errors.name && errors.name.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.start_date}>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="datetime-local"
                  {...register('start_date')}
                  min={getCurrentDateTime()}
                />
                <FormErrorMessage>
                  {errors.start_date && errors.start_date.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.end_date}>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="datetime-local"
                  {...register('end_date')}
                  min={getCurrentDateTime()}
                />
                <FormErrorMessage>
                  {errors.end_date && errors.end_date.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.location}>
                <FormLabel>Location</FormLabel>
                <Input
                  {...register('location')}
                  placeholder="Enter event location"
                />
                <FormErrorMessage>
                  {errors.location && errors.location.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.event_link}>
                <FormLabel>Event Link (Optional)</FormLabel>
                <Input
                  {...register('event_link')}
                  placeholder="https://example.com/event"
                  type="url"
                />
                <FormErrorMessage>
                  {errors.event_link && errors.event_link.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.description}>
                <FormLabel>Description (Optional)</FormLabel>
                <Textarea
                  {...register('description')}
                  placeholder="Enter event description"
                  rows={4}
                  maxH="200px"
                  overflowY="auto"
                />
                <FormErrorMessage>
                  {errors.description && errors.description.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.cover_image}>
                <FormLabel>Cover Image (Optional)</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  {...register('cover_image')}
                  p={1}
                />
                <FormErrorMessage>
                  {errors.cover_image && errors.cover_image.message}
                </FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                bg="#134A70"
                color="white"
                isLoading={isLoading}
                loadingText="Creating..."
                mt={4}
                _hover={{ bg: '#134A70' }}
              >
                Create Event
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateEventModal;
